import {
    getPartner,
    resolvePartnerTheme,
    type PartnerConfig,
    type ResolvedPartnerTheme,
} from "~/config/partners"

/**
 * Single source of truth for the co-branded ("partner") mode.
 *
 * Partner mode is a global session state driven by the `pay_form_filter`
 * cookie (set by `server/middleware/filterCookies.ts` from `?pay_form=<slug>`).
 * When the cookie matches a partner in `config/partners.ts`, the whole storefront
 * is rendered with that partner's chrome (top bar, nav, footer, palette) while
 * reusing the same pages/components.
 */
export const usePartnerBranding = () => {
    const partnerCookie = useCookie<string | null>("pay_form_filter")

    const activePartner = computed<PartnerConfig | null>(() =>
        getPartner(partnerCookie.value)
    )

    const isPartnerMode = computed<boolean>(() => activePartner.value !== null)

    return {
        partnerCookie,
        activePartner,
        isPartnerMode,
    }
}

/** Converts `#004482` (or `#048`) into the `"0 68 130"` channel form Tailwind needs. */
const hexToRgbChannels = (hex: string): string | null => {
    let h = hex.trim().replace("#", "")
    if (h.length === 3) {
        h = h
            .split("")
            .map((c) => c + c)
            .join("")
    }
    if (h.length !== 6) return null
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    if ([r, g, b].some((n) => Number.isNaN(n))) return null
    return `${r} ${g} ${b}`
}

/**
 * Applies (or clears) the partner color palette as CSS variables on the document
 * root. Client-only; safe to call when `document` is unavailable (no-op).
 *
 * Sets `--color-*-rgb` (channels) for Tailwind's primary/secondary and
 * `--q-*` (hex) for Quasar. Passing `null` reverts to the Dapamas defaults
 * defined in `assets/css/main.css`.
 */
/** Maps each resolved per-surface token to its CSS variable name. */
const SURFACE_VAR_MAP: Record<keyof ResolvedPartnerTheme, string> = {
    topbarBg: "--partner-topbar-bg",
    topbarText: "--partner-topbar-text",
    backButtonBg: "--partner-back-bg",
    backButtonText: "--partner-back-text",
    subheaderBg: "--partner-subheader-bg",
    subheaderText: "--partner-subheader-text",
    footerBg: "--partner-footer-bg",
    footerText: "--partner-footer-text",
    footerMuted: "--partner-footer-muted",
    footerLink: "--partner-footer-link",
}

export const applyPartnerTheme = (partner: PartnerConfig | null) => {
    if (typeof document === "undefined") return
    const root = document.documentElement
    const vars = [
        "--color-primary-rgb",
        "--color-secondary-rgb",
        "--color-on-primary",
        "--q-primary",
        "--q-secondary",
        ...Object.values(SURFACE_VAR_MAP),
    ]

    if (!partner) {
        vars.forEach((v) => root.style.removeProperty(v))
        return
    }

    const primaryRgb = hexToRgbChannels(partner.colorPrimary)
    const secondaryRgb = hexToRgbChannels(partner.colorSecondary)

    if (primaryRgb) root.style.setProperty("--color-primary-rgb", primaryRgb)
    if (secondaryRgb)
        root.style.setProperty("--color-secondary-rgb", secondaryRgb)
    root.style.setProperty("--color-on-primary", partner.colorText)
    root.style.setProperty("--q-primary", partner.colorPrimary)
    root.style.setProperty("--q-secondary", partner.colorSecondary)

    // Granular per-surface tokens (each override or its base-palette fallback).
    const theme = resolvePartnerTheme(partner)
    ;(Object.keys(SURFACE_VAR_MAP) as (keyof ResolvedPartnerTheme)[]).forEach(
        (key) => root.style.setProperty(SURFACE_VAR_MAP[key], theme[key])
    )
}
